<script setup lang="ts">
/**
 * On Board — 문서 패키지 (Stage 스테퍼, 흰 A4 페이지 크롬 + zoom). AgmPackageDoc 와 동일 UI.
 *   Stage 1 Directors (DR Change of Director + Form 45 per director) ·
 *   Stage 2 Secretary (DR Change of Secretary + Form 45B per secretary) ·
 *   Stage 3 Registered Office (DR Change in RO) · Stage 4 Service Agreement (bundle).
 */
import { computed, ref, watch } from 'vue';
import type { Component } from 'vue';
import DocScope from './cdd/DocScope.vue';
import OnBoardDrDirectorDoc from './docs/OnBoardDrDirectorDoc.vue';
import OnBoardDrSecretaryDoc from './docs/OnBoardDrSecretaryDoc.vue';
import OnBoardDrRoDoc from './docs/OnBoardDrRoDoc.vue';
import OnBoardForm45Doc from './docs/OnBoardForm45Doc.vue';
import OnBoardForm45bDoc from './docs/OnBoardForm45bDoc.vue';
import OnBoardBundleDoc from './docs/OnBoardBundleDoc.vue';

type ListKey = 'f45' | 'f45b';
type Slot =
  | { kind: 'fixed'; icon: string; name: string; comp: Component; key: string }
  | { kind: 'repeat'; icon: string; name: string; comp: Component; scope: string; id: number; listKey: ListKey };

interface StageDef { no: number; en: string; short: string; tools?: { key: ListKey; label: string }[]; buildSlots: () => Slot[]; }

const props = defineProps<{
  autoLocal?: { f45Ids?: number[]; f45bIds?: number[] } | null;
}>();

const f45Ids = ref<number[]>([]);
const f45bIds = ref<number[]>([]);
let f45Seq = 0;
let f45bSeq = 0;

watch(() => props.autoLocal, (v) => {
  if (!v) return;
  if (Array.isArray(v.f45Ids)) { f45Ids.value = [...v.f45Ids]; f45Seq = v.f45Ids.length ? Math.max(...v.f45Ids) : 0; }
  if (Array.isArray(v.f45bIds)) { f45bIds.value = [...v.f45bIds]; f45bSeq = v.f45bIds.length ? Math.max(...v.f45bIds) : 0; }
}, { deep: true, immediate: true });

function add(key: ListKey) {
  if (key === 'f45') { f45Seq += 1; f45Ids.value.push(f45Seq); }
  else { f45bSeq += 1; f45bIds.value.push(f45bSeq); }
}
function remove(key: ListKey, id: number) {
  if (key === 'f45') f45Ids.value = f45Ids.value.filter((x) => x !== id);
  else f45bIds.value = f45bIds.value.filter((x) => x !== id);
}

const activeStages = computed((): StageDef[] => {
  const stages: Omit<StageDef, 'no'>[] = [
    {
      en: 'Directors — Appointment / Resignation', short: 'Directors',
      tools: [{ key: 'f45', label: '+ Add Form 45 (per director)' }],
      buildSlots: () => [
        { kind: 'fixed', key: 'dr_dir', icon: '📝', name: "Directors' Resolutions — Change of Director", comp: OnBoardDrDirectorDoc },
        ...f45Ids.value.map((id) => ({ kind: 'repeat' as const, icon: '🪪', name: 'Form 45 — Consent to Act as Director', comp: OnBoardForm45Doc, scope: `f45_${id}/`, id, listKey: 'f45' as ListKey })),
      ],
    },
    {
      en: 'Secretary — Appointment / Resignation', short: 'Secretary',
      tools: [{ key: 'f45b', label: '+ Add Form 45B (per secretary)' }],
      buildSlots: () => [
        { kind: 'fixed', key: 'dr_sec', icon: '📝', name: "Directors' Resolutions — Change of Secretary", comp: OnBoardDrSecretaryDoc },
        ...f45bIds.value.map((id) => ({ kind: 'repeat' as const, icon: '🪪', name: 'Form 45B — Consent to Act as Secretary', comp: OnBoardForm45bDoc, scope: `f45b_${id}/`, id, listKey: 'f45b' as ListKey })),
      ],
    },
    {
      en: 'Registered Office', short: 'Reg. Office',
      buildSlots: () => [{ kind: 'fixed', key: 'dr_ro', icon: '🏢', name: "Directors' Resolutions — Change in Registered Office", comp: OnBoardDrRoDoc }],
    },
    {
      en: 'Corporate Secretarial Service Agreement', short: 'Service Agreement',
      buildSlots: () => [{ kind: 'fixed', key: 'bundle', icon: '📄', name: 'On Board — Service Agreement Bundle', comp: OnBoardBundleDoc }],
    },
  ];
  return stages.map((st, i) => ({ ...st, no: i + 1 }));
});

const currentStage = ref(1);
const totalStages = computed(() => activeStages.value.length);
function go(n: number) { if (n >= 1 && n <= totalStages.value) currentStage.value = n; }
watch(totalStages, (n) => { if (currentStage.value > n) currentStage.value = Math.max(1, n); });

const view = computed(() => {
  let n = 0;
  return activeStages.value.map((st) => ({
    no: st.no, en: st.en, short: st.short, tools: st.tools || [],
    docs: st.buildSlots().map((d) => ({ ...d, no: (n += 1) })),
  }));
});
const totalDocs = computed(() => view.value.reduce((s, st) => s + st.docs.length, 0));

const ZOOM_MIN = 100, ZOOM_MAX = 175, ZOOM_STEP = 25;
const docZoom = ref(100);
function setZoom(n: number) { docZoom.value = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP)); }

function gatherLocalState() { return { f45Ids: [...f45Ids.value], f45bIds: [...f45bIds.value], currentStage: currentStage.value }; }
function restoreLocalState(s: { f45Ids?: number[]; f45bIds?: number[]; currentStage?: number } | null) {
  if (!s) return;
  if (Array.isArray(s.f45Ids)) { f45Ids.value = [...s.f45Ids]; f45Seq = s.f45Ids.length ? Math.max(...s.f45Ids) : 0; }
  if (Array.isArray(s.f45bIds)) { f45bIds.value = [...s.f45bIds]; f45bSeq = s.f45bIds.length ? Math.max(...s.f45bIds) : 0; }
  if (s.currentStage && s.currentStage >= 1 && s.currentStage <= totalStages.value) currentStage.value = s.currentStage;
}
defineExpose({ gatherLocalState, restoreLocalState });
</script>

<template>
  <div class="agm-pkg">
    <div class="stepper">
      <button v-for="st in view" :key="st.no" type="button" class="step" :class="{ active: currentStage === st.no, done: currentStage > st.no }" @click="go(st.no)">
        <span class="step-no">{{ currentStage > st.no ? '✓' : st.no }}</span>
        <span class="step-txt"><b>Stage {{ st.no }}</b><small>{{ st.short }}</small></span>
      </button>
    </div>

    <div class="zoom-bar">
      <div class="doc-zoom">
        <button type="button" class="doc-zoom-btn" :disabled="docZoom <= ZOOM_MIN" @click="setZoom(docZoom - ZOOM_STEP)">A−</button>
        <button type="button" class="doc-zoom-pct" @click="setZoom(100)">{{ docZoom }}%</button>
        <button type="button" class="doc-zoom-btn" :disabled="docZoom >= ZOOM_MAX" @click="setZoom(docZoom + ZOOM_STEP)">A+</button>
      </div>
    </div>

    <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
      <section v-for="st in view" :key="st.no" v-show="currentStage === st.no" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage {{ st.no }}</span><span class="stage-t">{{ st.en }}</span></div>
        <div v-if="st.tools.length" class="agm-toolbar">
          <span class="agm-toolbar-lbl">Add document</span>
          <button v-for="tool in st.tools" :key="tool.key" type="button" class="agm-tool" @click="add(tool.key)">{{ tool.label }}</button>
        </div>
        <div v-for="d in st.docs" :key="d.kind === 'fixed' ? d.key : d.scope" class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">{{ d.icon }}</span><span class="nm">{{ d.name }}</span>
            <template v-if="d.kind === 'repeat'">
              <span class="copy">#{{ d.id }}</span>
              <button type="button" class="agm-rm" title="Remove copy" @click="remove(d.listKey, d.id)">×</button>
            </template>
            <span class="no">Doc {{ d.no }} / {{ totalDocs }}</span>
          </div>
          <div class="simple-frame">
            <DocScope v-if="d.kind === 'repeat'" :scope="d.scope"><component :is="d.comp" /></DocScope>
            <component v-else :is="d.comp" />
          </div>
        </div>
      </section>
    </div>

    <div class="stage-nav">
      <button v-if="currentStage > 1" type="button" class="nav-btn" @click="go(currentStage - 1)">← Previous</button>
      <span class="nav-count">Stage {{ currentStage }} / {{ totalStages }}</span>
      <button v-if="currentStage < totalStages" type="button" class="nav-btn primary" @click="go(currentStage + 1)">Next →</button>
      <button v-else type="button" class="nav-btn primary" disabled>All stages complete ✓</button>
    </div>
  </div>
</template>

<style scoped>
.agm-pkg { color: #111; }
.stepper { display: flex; gap: 8px; width: 794px; max-width: 100%; margin: 0 auto 12px; padding: 12px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20,30,60,0.06); box-sizing: border-box; }
.step { flex: 1 1 0; min-width: 0; display: flex; align-items: flex-start; gap: 8px; padding: 10px; border: 1px solid transparent; border-radius: 12px; background: #f6f8fc; cursor: pointer; text-align: left; }
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; }
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.step-txt b { font-size: 11.5px; color: #1f2d4d; white-space: nowrap; }
.step-txt small { font-size: 10px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.zoom-bar { display: flex; justify-content: flex-end; width: 794px; max-width: 100%; margin: 0 auto 18px; }
.doc-zoom { display: inline-flex; align-items: center; gap: 2px; background: #f2f4f6; border-radius: 10px; padding: 2px; }
.doc-zoom-btn, .doc-zoom-pct { border: none; background: transparent; cursor: pointer; font-weight: 800; color: #4b5563; border-radius: 8px; height: 32px; }
.doc-zoom-btn { min-width: 36px; font-size: 15px; }
.doc-zoom-btn:disabled { opacity: 0.35; cursor: default; }
.doc-zoom-pct { min-width: 52px; font-size: 12px; color: #3182f6; }
.paper-zoom { width: 794px; max-width: 100%; margin: 0 auto; }
.stage { width: 100%; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; padding: 4px 2px 14px; border-bottom: 1px solid #eef1f6; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 5px 12px; border-radius: 999px; }
.stage-t { font-weight: 800; font-size: 15px; color: #1f2d4d; }
.agm-toolbar { width: 100%; margin: 0 0 22px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; padding: 14px 16px; border: 1px solid #d7e0f0; border-radius: 12px; background: #f7f9fd; box-sizing: border-box; }
.agm-toolbar-lbl { font-size: 11.5px; font-weight: 800; color: #6b7a94; }
.agm-tool { border: 1px solid #1b84ff; background: #fff; color: #1b84ff; border-radius: 999px; padding: 7px 13px; font-size: 12px; font-weight: 700; cursor: pointer; }
.agm-tool:hover { background: #eaf3ff; }
.pkg-doc { width: 100%; }
.pkg-doc + .pkg-doc { margin-top: 36px; }
.pkg-doclabel { display: flex; align-items: center; gap: 8px; padding: 0 4px 12px; }
.pkg-doclabel .ic { font-size: 14px; }
.pkg-doclabel .nm { font-size: 12.5px; font-weight: 800; color: #42506a; }
.pkg-doclabel .copy { font-size: 10.5px; font-weight: 800; color: #1b84ff; background: #eaf3ff; border-radius: 999px; padding: 2px 8px; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.agm-rm { border: none; background: transparent; color: #c0392b; font-size: 18px; cursor: pointer; }
.simple-frame { width: 100%; background: #fff; border: 1px solid #e2e5ea; box-shadow: 0 4px 20px rgba(20,30,60,0.09); padding: 32px; box-sizing: border-box; overflow: hidden; font-size: 12px; }
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 16px; width: 794px; max-width: 100%; margin: 36px auto 12px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 11px 20px; cursor: pointer; }
.nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; }
.nav-btn:disabled { opacity: 0.55; cursor: default; }
.nav-count { font-size: 12.5px; font-weight: 800; color: #8a97b5; }
</style>
